import { getApiBaseUrl, getBackendBaseUrl } from '@/setup/apiBaseUrl';

/**
 * Utility functions for handling company logo URLs
 * Converts S3 URLs to local URLs to ensure logos are always served from backend
 */

const getBackendUrl = (): string => getBackendBaseUrl();

const getLogoApiBaseUrl = (): string => getApiBaseUrl();

/**
 * Convert S3 URL or any logo URL to local URL using backend URL from environment
 * @param logoUrl - The logo URL from API (could be S3 URL or local URL)
 * @returns Local URL path (e.g., http://backend-url/media/company_logos/logo.png or /media/company_logos/logo.png)
 */
export const getLocalLogoUrl = (logoUrl: string | undefined): string | undefined => {
  if (!logoUrl) return undefined;
  
  const backendUrl = getBackendUrl();
  
  
  // If already a full URL (starts with http:// or https:// and contains /media/), extract filename and use API endpoint
  if ((logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) && logoUrl.includes('/media/')) {
    // Extract filename from company_logos path
    const filenameMatch = logoUrl.match(/\/media\/company_logos\/(.+)$/);
    if (filenameMatch) {
      const filename = filenameMatch[1];
      const apiBaseUrl = getLogoApiBaseUrl();
      const result = `${apiBaseUrl}/companies/logos/${filename}`;
      return result;
    }
    // If it's already pointing to backend API endpoint, return as is
    if (logoUrl.includes('/api/companies/logos/')) {
      return logoUrl;
    }
  }
  
  // If already a local URL (starts with /media/), use API endpoint to serve logo
  if (logoUrl.startsWith('/media/')) {
    // Extract filename from path (e.g., /media/company_logos/m1.jpeg -> m1.jpeg)
    const filenameMatch = logoUrl.match(/\/media\/company_logos\/(.+)$/);
    if (filenameMatch) {
      const filename = filenameMatch[1];
      const apiBaseUrl = getLogoApiBaseUrl();
      const result = `${apiBaseUrl}/companies/logos/${filename}`;
      return result;
    }
    // Fallback: use direct media URL
    const result = backendUrl ? `${backendUrl}${logoUrl}` : logoUrl;
    return result;
  }
  
  // If it's an S3 URL, extract the file path and convert to local
  if (logoUrl.includes('s3.amazonaws.com') || logoUrl.includes('s3.')) {
    // Extract the path after /media/
    const mediaMatch = logoUrl.match(/\/media\/company_logos\/(.+)$/);
    if (mediaMatch) {
      const filename = mediaMatch[1];
      // Use API endpoint to serve logo
      const apiBaseUrl = getLogoApiBaseUrl();
      const result = `${apiBaseUrl}/companies/logos/${filename}`;
      return result;
    }
    // Fallback: try to extract filename from URL
    const filenameMatch = logoUrl.match(/\/([^/]+\.(png|jpg|jpeg|svg|gif|webp|jpeg))$/i);
    if (filenameMatch) {
      const filename = filenameMatch[1];
      // Use API endpoint to serve logo
      const apiBaseUrl = getLogoApiBaseUrl();
      const result = `${apiBaseUrl}/companies/logos/${filename}`;
      return result;
    }
  }
  
  // If it's a relative path, extract filename and use API endpoint
  if (logoUrl.startsWith('company_logos/')) {
    const filename = logoUrl.replace('company_logos/', '');
    const apiBaseUrl = getLogoApiBaseUrl();
    const result = `${apiBaseUrl}/companies/logos/${filename}`;
    return result;
  }
  
  // If it's a full URL but not S3, try to extract the path and use API endpoint
  if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
    const urlMatch = logoUrl.match(/\/media\/company_logos\/(.+)$/);
    if (urlMatch) {
      const filename = urlMatch[1];
      const apiBaseUrl = getLogoApiBaseUrl();
      const result = `${apiBaseUrl}/companies/logos/${filename}`;
      return result;
    }
  }
  
  // Return as is if we can't determine the format
  return logoUrl;
};

